import { useEffect, useState } from "react";
import { OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import cartImg from "../images/basket.svg";
import {
  cartProductsSelector,
  totalCartProducts,
  totalCartSelector,
} from "../modules/cart/selectors";
import { setAddressAndCost } from "../modules/delivery/actions";
import {
  deliverySelector,
  pickupLocationSelector,
} from "../modules/delivery/selectors";
import { Button } from "../styles";
import { client } from "../utils/api";
import { formatPrice } from "../utils/format";
import CartItem from "./cartItem";
import { Products } from "./styles";
import Modal from "./modal";
import {
  canCheckoutSelector,
  CHECKOUT_BLOCK_REASONS,
  checkoutBlockReasonSelector,
} from "../modules/checkout/selectors";
import { storeSelector } from "../modules/store/selectors";
import {
  DELIVERY_ADDRESS,
  DELIVERY_PICKUP,
  normalizeDeliveryCost,
} from "../modules/delivery/reducer";
import { ArrowRight, TriangleAlert } from "lucide-react";

function Cart() {
  const history = useHistory();
  const dispatch = useDispatch();
  const cart = useSelector(cartProductsSelector);
  const total = useSelector(totalCartSelector);
  const { minOrderValue } = useSelector(storeSelector);
  const cartTotal = useSelector(totalCartProducts);
  const delivery = useSelector(deliverySelector);
  const pickupLocation = useSelector(pickupLocationSelector);
  const canCheckout = useSelector(canCheckoutSelector);
  const checkoutBlockReason = useSelector(checkoutBlockReasonSelector);
  const [, setClockTick] = useState(0);
  const [showDeliveryCostChange, setShowDeliveryCostChange] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deliveryError, setDeliveryError] = useState("");

  useEffect(() => {
    const interval = window.setInterval(
      () => setClockTick((value) => value + 1),
      30 * 1000,
    );
    return () => window.clearInterval(interval);
  }, []);

  const checkoutBlockMessages = {
    [CHECKOUT_BLOCK_REASONS.MENU_UNAVAILABLE]:
      "Poczekaj na wczytanie menu dla wybranego dnia. Jeśli wystąpił błąd, spróbuj ponownie.",
    [CHECKOUT_BLOCK_REASONS.STORE_CLOSED]:
      "Sklep jest teraz zamknięty. Zamówienie złożysz w godzinach otwarcia.",
    [CHECKOUT_BLOCK_REASONS.DELIVERY_TYPE_REQUIRED]:
      "Wybierz dowóz albo odbiór osobisty.",
    [CHECKOUT_BLOCK_REASONS.ADDRESS_REQUIRED]:
      "Podaj adres dostawy i sprawdź, czy znajduje się w obszarze dowozu.",
    [CHECKOUT_BLOCK_REASONS.PICKUP_LOCATION_REQUIRED]:
      "Wybierz punkt odbioru zamówienia.",
    [CHECKOUT_BLOCK_REASONS.MIN_ORDER_VALUE]: `Dodaj do koszyka produkty za jeszcze ${formatPrice(
      Math.max(0, minOrderValue - cartTotal),
    )} zł, aby skorzystać z dowozu.`,
  };

  function navigate2Checkout() {
    history.push("/checkout");
  }

  async function checkout() {
    if (!canCheckout) {
      return;
    }

    if (delivery.type === DELIVERY_ADDRESS) {
      setLoading(true);
      setDeliveryError("");
      try {
        const location = {
          address: delivery.address,
          place_id: delivery.placeId,
          latitude: delivery.latitude,
          longitude: delivery.longitude,
        };
        const response = await client("store/distance", {
          method: "POST",
          body: location,
        });
        dispatch(setAddressAndCost({ ...location, ...response }));
        if (normalizeDeliveryCost(response.cost) !== delivery.cost) {
          setShowDeliveryCostChange(true);
        } else {
          navigate2Checkout();
        }
      } catch (error) {
        setDeliveryError(
          error?.json?.detail ||
            "Nie udało się ponownie sprawdzić kosztu dostawy. Spróbuj jeszcze raz.",
        );
      } finally {
        setLoading(false);
      }
    } else {
      navigate2Checkout();
    }
  }

  if (cart.length === 0) {
    return (
      <div className="empty-cart text-center">
        <img src={cartImg} alt="Empty cart" style={{ width: "75px" }} />
        <h4>Twój koszyk jest pusty</h4>
        <p className="mt-1">Spróbuj wybrać coś dla siebie</p>
      </div>
    );
  }

  return (
    <>
      <Products>
        {cart.map((product) => (
          <CartItem key={product.id} product={product} />
        ))}
        {delivery.type === DELIVERY_ADDRESS &&
          minOrderValue !== null &&
          cartTotal < minOrderValue && (
            <div className="cart-delivery-notice">
              <span>
                <TriangleAlert size={16} className="me-2" /> Do minimum dla
                dowozu brakuje
              </span>
              <strong>
                {formatPrice(Math.max(0, minOrderValue - cartTotal))} zł
              </strong>
            </div>
          )}
        <div className="cart-summary">
          {deliveryError && (
            <div className="cart-delivery-error">{deliveryError}</div>
          )}
          {delivery.type === DELIVERY_ADDRESS && delivery.cost !== null && (
            <div className="cart-summary__delivery">
              <span>
                Dowóz
                {delivery.distance !== null && (
                  <small>{Number(delivery.distance).toFixed(1)} km</small>
                )}
              </span>
              <strong>{formatPrice(delivery.cost)} zł</strong>
            </div>
          )}
          {delivery.type === DELIVERY_PICKUP && pickupLocation && (
            <div className="cart-summary__delivery">
              <span>Odbiór osobisty</span>
              <strong>bezpłatnie</strong>
            </div>
          )}
          <div className="cart-summary__total">
            <span>Razem</span>
            <strong>{formatPrice(total)} zł</strong>
          </div>
          {loading ? (
            <Spinner animation="border" />
          ) : canCheckout ? (
            <Button active onClick={checkout}>
              Do kasy <ArrowRight size={16} className="ms-2" />
            </Button>
          ) : (
            <OverlayTrigger
              placement="top"
              trigger={["hover", "focus", "click"]}
              overlay={
                <Tooltip id="checkout-block-reason">
                  {checkoutBlockMessages[checkoutBlockReason]}
                </Tooltip>
              }
            >
              <Button
                onClick={checkout}
                aria-disabled="true"
                aria-describedby="checkout-block-reason"
                active={false}
              >
                Do kasy <ArrowRight size={16} className="ms-2" />
              </Button>
            </OverlayTrigger>
          )}
        </div>
      </Products>
      <Modal
        open={showDeliveryCostChange}
        onClose={() => setShowDeliveryCostChange(false)}
        className="text-center"
      >
        <div style={{ fontSize: "55px" }}>🚚</div>
        <h5>Koszty dostawy</h5>
        <div>
          {delivery.cost !== null
            ? `Koszt dostawy do ${delivery.address} zmienił się i aktualnie wynosi ${delivery.cost} zł.`
            : `Adres ${delivery.address} jest poza obszarem dowozu.`}
        </div>
        <Button
          secondary
          big
          onClick={
            delivery.cost !== null
              ? navigate2Checkout
              : () => setShowDeliveryCostChange(false)
          }
          className="mt-4"
        >
          {delivery.cost !== null ? "Ok, zgoda" : "Zmień adres"}
        </Button>
      </Modal>
    </>
  );
}

export default Cart;
